import { config } from "@/config/env";
import { getAuthClient } from "@/lib/auth";
import type { UIMessage, UIMessageChunk } from "ai";

// Must match a model in billing's AI model list, or usage is metered at the 1.0 fallback.
export const DEFAULT_CHAT_MODEL: string = "claude-sonnet-5-5";

let messageIdCounter = 0;
const nextId = () => `msg-${++messageIdCounter}-${Date.now()}`;

/**
 * Custom ChatTransport for the Anythink AI Sidebar API.
 * Connects the Vercel AI SDK's useChat hook to our SSE-based backend.
 */
export interface AIUsageInfo {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  model?: string;
  is_byok: boolean;
}

export class AnythinkChatTransport {
  private _onUsage?: (usage: AIUsageInfo) => void;
  private _lastUsage: AIUsageInfo | null = null;

  set onUsage(handler: (usage: AIUsageInfo) => void) {
    this._onUsage = handler;
  }

  get lastUsage(): AIUsageInfo | null {
    return this._lastUsage;
  }

  async sendMessages({
    messages,
    abortSignal,
    body,
  }: {
    trigger: string;
    chatId: string;
    messageId?: string;
    messages: UIMessage[];
    abortSignal: AbortSignal;
    headers?: Record<string, string>;
    body?: Record<string, unknown>;
    metadata?: unknown;
  }): Promise<ReadableStream<UIMessageChunk>> {
    const token = getAuthClient().getAccessToken();
    if (!token) throw new Error("Authentication required");

    const tenantId = this.getCurrentOrgId();
    if (!tenantId) throw new Error("No organisation selected");

    const url = config.sidebarApiUrl
      ? `${config.sidebarApiUrl}/api/chat`
      : `${config.instanceUrl}/org/${tenantId}/integrations/ai/chat`;

    const attachments = body?.attachments as
      | Array<{ mediaType: string; base64Data: string; fileName: string }>
      | undefined;

    const apiMessages = messages.map((m, i) => {
      const text =
        m.parts
          ?.filter(
            (p): p is { type: "text"; text: string } => p.type === "text",
          )
          .map((p) => p.text)
          .join("") ?? "";

      // Attach files to the last user message
      const isLastMessage = i === messages.length - 1;
      if (
        isLastMessage &&
        attachments &&
        attachments.length > 0 &&
        m.role === "user"
      ) {
        return { role: m.role, content: text, attachments };
      }
      return { role: m.role, content: text };
    });

    const model = (body?.model as string) || DEFAULT_CHAT_MODEL;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
        "X-Current-Org-Id": tenantId,
      },
      body: JSON.stringify({
        messages: apiMessages,
        model,
        enableTools: true,
      }),
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error("Response body is not readable");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const textPartId = nextId();
    const self = this;

    // Push-based streaming — read from SSE and push chunks as they arrive
    return new ReadableStream<UIMessageChunk>({
      start(controller) {
        let started = false;
        let buffer = "";

        async function pump(): Promise<void> {
          try {
            const { done, value } = await reader.read();

            if (done) {
              if (started) {
                controller.enqueue({ type: "text-end", id: textPartId });
              }
              controller.enqueue({ type: "finish" });
              controller.close();
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const dataJson = line.slice(6).trim();
              if (!dataJson) continue;

              try {
                const data = JSON.parse(dataJson);

                if (data.type === "chunk" && data.content) {
                  if (!started) {
                    controller.enqueue({
                      type: "text-start",
                      id: textPartId,
                    });
                    started = true;
                  }
                  controller.enqueue({
                    type: "text-delta",
                    delta: data.content,
                    id: textPartId,
                  });
                } else if (data.type === "complete") {
                  if (started) {
                    controller.enqueue({
                      type: "text-end",
                      id: textPartId,
                    });
                  }
                  controller.enqueue({ type: "finish" });
                  controller.close();
                  return;
                } else if (data.type === "usage") {
                  // Handle both formats: { usage: {...} } or { content: "{...}" }
                  const usage =
                    data.usage ||
                    (data.content ? JSON.parse(data.content) : null);
                  if (usage) {
                    self._lastUsage = usage as AIUsageInfo;
                    self._onUsage?.(usage as AIUsageInfo);
                  }
                } else if (data.type === "error") {
                  const error = new Error(data.error || "An unknown error occurred");
                  (error as any).code = data.errorCode;
                  controller.error(error);
                  return;
                }
              } catch {
                // Skip malformed JSON
              }
            }

            // Continue reading
            return pump();
          } catch (err) {
            controller.error(err);
          }
        }

        pump();
      },
      cancel() {
        reader.cancel();
      },
    });
  }

  async reconnectToStream(_options: {
    chatId: string;
    [key: string]: unknown;
  }): Promise<ReadableStream<UIMessageChunk> | null> {
    return null;
  }

  private getCurrentOrgId(): string | null {
    if (typeof window === "undefined") return null;
    const match = window.location.pathname.match(/^\/org\/(\d+)/);
    return match ? match[1] : null;
  }
}
